import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

/**
 * A grid of soft red dots that breathes ambiently and ripples concentrically
 * out from the cursor — a subtle "voice agent listening" backdrop. Pure
 * canvas, no deps. Respects prefers-reduced-motion (renders a single static
 * frame instead of animating).
 *
 * Drop into a page as a sibling and lift the foreground above it with
 * `relative z-10`:
 *
 *   <div className="relative">
 *     <AudioRippleBackground />
 *     <div className="relative z-10">…content…</div>
 *   </div>
 */
export function AudioRippleBackground({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Tunables — kept here, not props, so the visual is consistent everywhere.
    const SPACING = 32; // px between dots (CSS pixels)
    const BASE_RADIUS = 1.1;
    const MAX_RADIUS = 4.0;
    const RIPPLE_RADIUS = 220; // cursor influence radius
    const BASE_ALPHA = 0.14;
    const MAX_ALPHA = 0.85;
    const RGB = "235, 23, 0"; // J&J red

    let width = 0;
    let height = 0;
    let dpr = 1;
    // Start cursor offscreen so first frame is calm; lerp toward the real
    // pointer for a soft "follow" feel rather than instant snap.
    let cursorX = -10000;
    let cursorY = -10000;
    let targetX = -10000;
    let targetY = -10000;
    let raf = 0;
    const start = performance.now();

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onMove = (e: PointerEvent) => {
      targetX = e.clientX;
      targetY = e.clientY;
    };

    const onLeave = () => {
      targetX = -10000;
      targetY = -10000;
    };

    const draw = () => {
      const t = (performance.now() - start) / 1000;

      // Easy lerp toward the pointer position so the ripple trails a hair
      // behind the cursor — feels organic rather than jittery.
      cursorX += (targetX - cursorX) * 0.18;
      cursorY += (targetY - cursorY) * 0.18;

      ctx.clearRect(0, 0, width, height);

      const cols = Math.ceil(width / SPACING) + 2;
      const rows = Math.ceil(height / SPACING) + 2;
      const offsetX = (width - (cols - 1) * SPACING) / 2;
      const offsetY = (height - (rows - 1) * SPACING) / 2;

      const screenCx = width / 2;
      const screenCy = height / 2;

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = offsetX + i * SPACING;
          const y = offsetY + j * SPACING;

          // Ambient: a slow concentric wave from the screen center so the
          // grid breathes even when no one's moving the mouse.
          const dCenter = Math.hypot(x - screenCx, y - screenCy);
          const ambient = reduceMotion ? 0 : 0.5 + 0.5 * Math.cos(dCenter * 0.014 - t * 1.4);

          // Cursor ripple: concentric wave centered on the (lerped) pointer,
          // attenuated by distance. This is what makes it feel like an audio
          // ripple emanating from where you're hovering.
          const dCursor = Math.hypot(x - cursorX, y - cursorY);
          let cursor = 0;
          if (dCursor < RIPPLE_RADIUS) {
            const falloff = 1 - dCursor / RIPPLE_RADIUS;
            const wave = 0.5 + 0.5 * Math.cos(dCursor * 0.045 - t * 5);
            // ease-out the falloff (smoother edge), keep wave amplitude full
            cursor = falloff * falloff * (0.55 + 0.45 * wave);
          }

          // Cursor dominates; ambient adds a soft baseline so the rest of
          // the screen is alive too.
          const intensity = Math.min(1, cursor + ambient * 0.22);
          const radius = BASE_RADIUS + (MAX_RADIUS - BASE_RADIUS) * intensity;
          const alpha = BASE_ALPHA + (MAX_ALPHA - BASE_ALPHA) * intensity;

          ctx.fillStyle = `rgba(${RGB}, ${alpha})`;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      if (!reduceMotion) {
        raf = requestAnimationFrame(draw);
      }
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerleave", onLeave);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={cn("pointer-events-none fixed inset-0", className)}
    />
  );
}
